import React, { useState, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useToast } from "@/hooks/use-toast";
import { apiService } from '@/config/api';
import { Button } from '@/components/ui/button';
import { Toaster } from "@/components/ui/toaster";
import { Loader2, ArrowRight, Settings, Mail, Phone, MapPin, Sparkles, FileText, Users, Target, Search, PenTool, Eye, Shield, UserCheck, Lock } from 'lucide-react';
import RobotChatIcon from '@/components/RobotChatIcon';
import CoverLetterIcon from '@/components/CoverLetterIcon';
import RecruiterLensIcon from '@/components/RecruiterLensIcon';

import { AnimatedDropbox } from '@/components/AnimatedDropbox';
import { getOrSetSessionId } from '@/lib/session';
import { logAiFeatureUsage } from '@/lib/logging';
import { Link, useNavigate } from 'react-router-dom';

// Directly import components that were causing issues with Suspense
import { ConfirmationModalContent } from '@/components/ConfirmationModalContent';
import { JobDescriptionDialog } from '@/components/JobDescriptionDialog';
import { ResultsModalContent } from '@/components/ResultsModalContent';
import  ConsentDialog  from '@/components/ConsentDialog';

import VTechLogo from '@/components/VTechLogo';

// Import ParsedDetailsDisplay directly since it's now client-safe
import ParsedDetailsDisplay from '@/components/ParsedDetailsDisplay';

// Import LoadingOverlay for better loading feedback
import { LoadingOverlay } from '@/components/LoadingOverlay';



// AI flows are now handled via API routes
import type { AnalyzeResumeInput, ParseResumeDetailsOutput } from '@/types/ai';

export default function Home() {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isLoadingConsent, setIsLoadingConsent] = useState(true);
  const [hasConsented, setHasConsented] = useState(false);
  const [showParsedDetails, setShowParsedDetails] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const [parsedKey, setParsedKey] = useState(0);

  const [selectedAction, setSelectedAction] = useState<AnalyzeResumeInput['mode'] | null>(null);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [parsedResumeDetails, setParsedResumeDetails] = useState<ParseResumeDetailsOutput | null>(null);
  const [maskedResumeText, setMaskedResumeText] = useState<string | null>(null);
  const [currentResumeId, setCurrentResumeId] = useState<string | null>(null);
  const [sessionToken, setSessionToken] = useState<string | null>(null);

  const [isLoadingAnalysis, setIsLoadingAnalysis] = useState(false);
  const [isResultsModalOpen, setIsResultsModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isJobDescriptionDialogOpen, setIsJobDescriptionDialogOpen] = useState(false);
  const [currentJobDescription, setCurrentJobDescription] = useState<string>("");

  const [resultsModalContent, setResultsModalContent] = useState<{ isLoading: boolean; error: string | null; analysisResult: string | null; action: string | null }>({
    isLoading: false, error: null, analysisResult: null, action: null,
  });
  
  const [currentUser, setCurrentUser] = useState<any | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();
  
  // Add loading states for navigation
  const [isNavigatingToToolbox, setIsNavigatingToToolbox] = useState(false);
  const [isNavigatingToAdmin, setIsNavigatingToAdmin] = useState(false);
  
  // Add a ref to track if the component is mounted
  const isMountedRef = useRef(true);

  // Add global error handler for unhandled promise rejections
  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('Unhandled promise rejection:', event.reason);
      // Prevent the default browser behavior
      event.preventDefault();
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  useEffect(() => {
    // Initialize session ID and consent state
    try {
      setSessionId(getOrSetSessionId());
      const consentGiven = localStorage.getItem('vtechLabsConsentGiven');
      if (consentGiven === 'true') {
          setHasConsented(true);
      }
    } catch (error) {
      console.error('Error initializing session or consent:', error);
    } finally {
      setIsLoadingConsent(false);
    }

    // TODO: Implement backend-based authentication when available
    // For now, we'll skip Firebase auth setup
  }, []);

  // Cleanup effect to reset the mounted ref when component unmounts
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleConsentAgree = (optIn: boolean) => {
      localStorage.setItem('vtechLabsConsentGiven', 'true');
      localStorage.setItem('vtechLabsRecruiterOptIn', String(optIn));
      setHasConsented(true);
      toast({
          title: "Consent Acknowledged",
          description: "Welcome to vTech Labs! You can now use the application.",
      });
  };

const handleFileUploadComplete = useCallback((
  success: boolean,
  fileName: string | null,
  parsedDetails: ParseResumeDetailsOutput | null,
  maskedTextValue: string | null,
  resumeId: string | null,
  sessionTokenValue: string | null
) => {
  if (success) {
    setUploadedFileName(fileName);
    setParsedResumeDetails(parsedDetails);
    setParsedKey(prev => prev + 1);
    setMaskedResumeText(maskedTextValue);
    setCurrentResumeId(resumeId);
    setSessionToken(sessionTokenValue);
    
    // Store uploaded resume data in sessionStorage for ResumeEditorPage access
    const uploadedResumeData = {
      fileName,
      parsedDetails,
      maskedTextValue,
      resumeId,
      sessionTokenValue,
      uploadedAt: new Date().toISOString()
    };
    sessionStorage.setItem('vtechUploadedResumeData', JSON.stringify(uploadedResumeData));
    
    toast({
      title: "Resume Ready",
      description: `${fileName} has been uploaded and parsed. Select an action below.`,
      duration: 5000,
    });
  } else {
    setUploadedFileName(null);
    setParsedResumeDetails(null);
    setMaskedResumeText(null);
    setCurrentResumeId(null);
    setSessionToken(null);
    
    // Clear uploaded resume data from sessionStorage
    sessionStorage.removeItem('vtechUploadedResumeData');
    
    toast({ title: "Upload Failed", description: "Please try again." });
  }
}, [toast]);

  const critiqueBot = "Resume Critique Bot";
  const coverLetterGen = "Cover Letter Generator";
  const recruiterView = "Recruiter Lens";

  const actionToModeMap: Record<string, AnalyzeResumeInput['mode']> = { 
    [critiqueBot]: 'critique',
    [coverLetterGen]: 'coverletter',
    [recruiterView]: 'recruiterview',
  };

  const handleActionSelect = (actionName: string) => {
    const mode = actionToModeMap[actionName];
    if (!mode) {
        toast({ title: "Invalid Action", variant: "destructive" });
        return;
    }
    setSelectedAction(mode);
    if (!maskedResumeText) {
      toast({
        title: "Upload Resume First",
        description: `Please upload your resume to use the '${actionName}' feature.`,
      });
      return;
    }
    if (mode === 'coverletter') {
      setIsJobDescriptionDialogOpen(true);
    } else {
      setIsConfirmModalOpen(true);
    }
  };

  const handleRunAnalysisConfirmed = async () => {
    if (!maskedResumeText || !selectedAction) {
      toast({ variant: "destructive", title: "Cannot Run Analysis", description: "Missing resume text or selected action." });
      setIsConfirmModalOpen(false);
      return;
    }
    setIsLoadingAnalysis(true);
    setResultsModalContent({ isLoading: true, error: null, analysisResult: null, action: selectedAction });
    setIsConfirmModalOpen(false);
    setIsResultsModalOpen(true);
    
    const input: AnalyzeResumeInput = {
        resumeText: maskedResumeText,
        mode: selectedAction,
        ...(selectedAction === 'coverletter' && currentJobDescription && { jobDescription: currentJobDescription })
    };
        try {
      // Call the API route instead of importing the function directly
      const headers = {
        'Content-Type': 'application/json',
        'X-Session-ID': sessionId || 'unknown',
        ...(sessionToken && { 'X-Session-Token': sessionToken }),
      };
      
      const response = await fetch(`${import.meta.env.VITE_API_URL || window.location.origin}/api/analyze-resume`, {
        method: 'POST',
        headers,
        body: JSON.stringify(input),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to analyze resume");
      }

      const result = await response.json();
      if (result.resumeId) {
  localStorage.setItem("resumeId", result.resumeId);
}
      
      if (result?.analysisResult) {
        setResultsModalContent({ isLoading: false, error: null, analysisResult: result.analysisResult, action: selectedAction });

        // Log AI feature usage in the background
        try {
          logAiFeatureUsage({
            feature: `analyzeResume:${selectedAction}`,
            input: { ...input, resumeText: `Length: ${input.resumeText.length}` }, // Avoid logging full resume text again
            output: { analysisResult: `Length: ${result.analysisResult.length}`},
            sessionId: sessionId ?? 'unknown',
          });
        } catch (error) {
          console.error('Failed to log AI feature usage:', error);
        }

        // TODO: Save analysis result to backend when endpoint is available

      } else {
        throw new Error("The AI analysis completed but returned no specific feedback.");
      }
    } catch (error: any) {
      const errorMessage = `Analysis failed: ${error.message ?? 'Unknown error'}.`;
      setResultsModalContent({ isLoading: false, error: errorMessage, analysisResult: null, action: selectedAction });
    } finally {
      setIsLoadingAnalysis(false);
      setCurrentJobDescription("");
    }
  };

 const handleReparse = () => {
  setUploadedFileName(null);
  setParsedResumeDetails(null);
  setMaskedResumeText(null);
  setSelectedAction(null);
  setCurrentResumeId(null);
  setSessionToken(null);
  setIsResultsModalOpen(false);
  setResultsModalContent({ isLoading: false, error: null, analysisResult: null, action: null });
  setShowParsedDetails(false); // <-- hide ParsedDetailsDisplay
  setResetKey(prev => prev + 1); // <-- re-render AnimatedDropbox
  
  // Clear uploaded resume data from sessionStorage
  sessionStorage.removeItem('vtechUploadedResumeData');
  
  toast({ title: "Ready for new upload", description: "Please upload a new resume." });
};

  // Navigation handlers with loading states
  const handleNavigateToToolbox = () => {
    setIsNavigatingToToolbox(true);
    navigate('/tech-playground');
  };

  const handleNavigateToAdmin = () => {
    setIsNavigatingToAdmin(true);
    navigate('/admin-login');
  };

  if (isLoadingConsent) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="text-center space-y-4"
        >
          <div className="p-4 bg-primary/10 rounded-full w-20 h-20 mx-auto flex items-center justify-center">
            <Shield className="h-10 w-10 text-primary" />
          </div>
          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-foreground">Initializing vTech Labs</h2>
            <p className="text-muted-foreground">Setting up your secure environment...</p>
          </div>
        </motion.div>
      </div>
    );
  }

 if (!hasConsented) {
  return (
    <div className="flex flex-col min-h-screen bg-background text-foreground items-center justify-center">
      <ConsentDialog isOpen={true} onAgree={handleConsentAgree} />
    </div>
  );
}

  const actionCards = [
    {
      title: critiqueBot,
      description: "Get detailed feedback on your resume structure, content, and impact",
      icon: RobotChatIcon,
      color: "resume-critique",
      colorClass: "resume-critique-color",
      bgClass: "resume-critique-bg",
      borderClass: "resume-critique-border",
      gradientClass: "resume-critique-gradient"
    },
    {
      title: coverLetterGen,
      description: "Generate tailored cover letters for specific job opportunities",
      icon: CoverLetterIcon,
      color: "cover-letter",
      colorClass: "cover-letter-color",
      bgClass: "cover-letter-bg",
      borderClass: "cover-letter-border",
      gradientClass: "cover-letter-gradient"
    },
    {
      title: recruiterView,
      description: "See your resume through a recruiter's perspective",
      icon: RecruiterLensIcon,
      color: "recruiter-lens",
      colorClass: "recruiter-lens-color",
      bgClass: "recruiter-lens-bg",
      borderClass: "recruiter-lens-border",
      gradientClass: "recruiter-lens-gradient"
    },

  ];

  return (
    <div className="flex flex-col min-h-screen bg-main-bg text-foreground relative">
      <Toaster />
      
      {/* Mouse-Driven Background Effects */}
    
      
      {/* Loading Overlay for Navigation */}
      <LoadingOverlay 
        isVisible={isNavigatingToToolbox || isNavigatingToAdmin}
        message={isNavigatingToToolbox ? "Loading AI Toolbox..." : "Loading Admin Panel..."}
      />
      
      {/* Render modals directly. Their visibility is controlled by their respective `isOpen` props. */}
      <ResultsModalContent
          isOpen={isResultsModalOpen}
          onClose={() => setIsResultsModalOpen(false)}
          isLoading={resultsModalContent.isLoading}
          error={resultsModalContent.error}
          analysisResult={resultsModalContent.analysisResult}
          action={resultsModalContent.action}
      />
      <ConfirmationModalContent
          isOpen={isConfirmModalOpen}
          onClose={() => setIsConfirmModalOpen(false)}
          onConfirm={handleRunAnalysisConfirmed}
          fileName={uploadedFileName}
          action={Object.keys(actionToModeMap).find(key => actionToModeMap[key] === selectedAction) ?? String(selectedAction)}
          isAnalyzing={isLoadingAnalysis}
      />
      <JobDescriptionDialog
        isOpen={isJobDescriptionDialogOpen}
        onClose={() => setIsJobDescriptionDialogOpen(false)}
        onSubmit={(jd) => {
          setCurrentJobDescription(jd);
          setIsJobDescriptionDialogOpen(false);
          setIsConfirmModalOpen(true);
        }}
        isSubmitting={isLoadingAnalysis}
      />

      {/* Header */}
      <motion.header 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="border-b border-border/50 bg-main-bg/80 backdrop-blur-sm sticky top-0 z-40 relative"
      >
        <div className="container-professional px-4 py-4 flex justify-between items-center">
          <VTechLogo />
          <div className="flex items-center gap-3">
            <motion.div 
              whileHover={{ scale: 1.02 }} 
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 10 }}
            >
              <button 
                className="btn-liquid-enhanced px-4 py-2 text-sm text-white" 
                onClick={handleNavigateToToolbox} 
                disabled={isNavigatingToToolbox}
              >
                {isNavigatingToToolbox ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  "AI Toolbox"
                )}
              </button>
            </motion.div>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >

            </motion.div>
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <main className="flex-grow bg-main-bg relative z-10">
        {/* Hero Section */}
        <section className="section-padding relative overflow-hidden">
          {/* Background Elements */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5"></div>
          <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl"></div>
          
          <div className="container-professional text-center relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="max-w-5xl mx-auto"
            >
              {/* Welcome Badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="inline-flex items-center space-x-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-2 mb-6"
              >
                <Sparkles className="h-4 w-4 text-white" />
                <span className="text-sm font-medium text-white">Welcome to vTech Labs</span>
              </motion.div>

              {/* Main Title */}
              <motion.h1 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-5xl md:text-7xl font-bold mb-6 text-foreground /90 to-accent bg-clip-text "
              >
                Professional Resume
                <br />
                <span className="text-foreground">Analysis</span>
              </motion.h1>

              {/* Subtitle */}
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="text-xl md:text-2xl text-muted-foreground mb-8 leading-relaxed max-w-3xl mx-auto"
              >
                Leverage cutting-edge AI technology to optimize your resume, generate compelling cover letters, and gain insights into how recruiters perceive your professional profile.
              </motion.p>

              {/* Feature Highlights */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
                className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12"
              >
                                 <div className="flex flex-col items-center space-y-3 p-4 rounded-lg action-card-liquid">
                   <div className="w-12 h-12 bg-orange-warm/10 rounded-full flex items-center justify-center">
                     <Target className="h-6 w-6 text-orange-warm" />
                   </div>
                   <h3 className="font-semibold text-foreground">AI-Powered Analysis</h3>
                   <p className="text-sm text-muted-foreground text-center">Advanced algorithms provide detailed insights and recommendations</p>
                 </div>
                
                <div className="flex flex-col items-center space-y-3 p-4 rounded-lg action-card-liquid">
                  <div className="w-12 h-12 bg-green-500/10 rounded-full flex items-center justify-center">
                    <Lock className="h-6 w-6 text-green-500" />
                  </div>
                  <h3 className="font-semibold text-foreground">Privacy Protected</h3>
                  <p className="text-sm text-muted-foreground text-center">Your data is securely processed with enterprise-grade protection</p>
                </div>
                
                <div className="flex flex-col items-center space-y-3 p-4 rounded-lg action-card-liquid">
                  <div className="w-12 h-12 bg-purple-500/10 rounded-full flex items-center justify-center">
                    <UserCheck className="h-6 w-6 text-purple-500" />
                  </div>
                  <h3 className="font-semibold text-foreground">Recruiter Insights</h3>
                  <p className="text-sm text-muted-foreground text-center">Understand how hiring managers view your application</p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Main Interface */}
        <section className="section-padding pt-0">
          <div className="container-professional">
            <div className="max-w-6xl">
              <div className="grid lg:grid-cols-2 gap-8 items-start">
                {/* Action Cards */}
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                  className="space-y-4"
                >
                  <h2 className="text-2xl font-semibold mb-6">
                    Choose Your Analysis
                  </h2>
                  {actionCards.map((card, index) => (
                    <motion.div
                      key={card.title}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
                      whileHover={{ scale: 1.02, y: -8 }}
                      whileTap={{ scale: 0.98, y: -4 }}
                                             className={`action-card-button rounded-full ${card.color}-button`}
                      onClick={() => handleActionSelect(card.title)}
                    >
                      <div className="flex items-start space-x-4">
                        {card.title === critiqueBot || card.title === coverLetterGen || card.title === recruiterView ? (
                          <div className="icon-container">
                            <card.icon className={`h-8 w-8 ${card.colorClass}`} />
                          </div>
                        ) : (
                          <div className={`p-3 rounded-lg bg-gradient-to-r ${card.color} text-white`}>
                            <card.icon className="h-6 w-6" />
                          </div>
                        )}
                        <div className="flex-1 button-text">
                                                                                <h3 className={`font-bold text-base mb-1 ${card.colorClass}`}>
                             {card.title}
                           </h3>
                           <p className="text-muted-foreground text-xs leading-relaxed">
                             {card.description}
                           </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>

                {/* Upload Area */}
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 0.6 }}
                  className="action-card-liquid p-8 rounded-xl hover:shadow-2xl"
                >
                  <div className="mb-6">
                    <h2 className="text-2xl font-semibold mb-2">Upload Your Resume</h2>
                    <p className="text-muted-foreground">
                      Supported formats: PDF, DOCX, TXT
                    </p>
                  </div>
                  <AnimatedDropbox 
                    key={resetKey} 
                    onFileUploadComplete={handleFileUploadComplete} 
                    isParentProcessing={isLoadingAnalysis} 
                  />
                </motion.div>
              </div>
            </div>
          </div>
        </section>

                 {/* Parsed Details Section */}
         <AnimatePresence>
           {parsedResumeDetails && (
             <motion.section
               initial={{ opacity: 0, height: 0 }}
               animate={{ opacity: 1, height: "auto" }}
               exit={{ opacity: 0, height: 0 }}
               transition={{ duration: 0.5 }}
               className="section-padding pt-0"
             >
               <div className="container-professional">
                                   {(() => {
                    return (
                     <ParsedDetailsDisplay
                       key={parsedKey}
                       details={{
                         name: parsedResumeDetails.name === null ? 'Resume Details' : (parsedResumeDetails.name || 'Resume Details'),
                         email: parsedResumeDetails.email,
                         phone: parsedResumeDetails.phone,
                         location: parsedResumeDetails.location,
                         linkedin: parsedResumeDetails.linkedin,
                         github: parsedResumeDetails.github,
                         portfolio: parsedResumeDetails.portfolio,
                         summary: parsedResumeDetails.summary,
                         skills: parsedResumeDetails.skills || [],
                         technicalSkills: parsedResumeDetails.technicalSkills || [],
                         softSkills: parsedResumeDetails.softSkills || [],
                         experience: parsedResumeDetails.experience || [],
                         education: parsedResumeDetails.education || [],
                         certifications: parsedResumeDetails.certifications || [],
                         projects: parsedResumeDetails.projects || [],
                         languages: parsedResumeDetails.languages || [],
                         interests: parsedResumeDetails.interests || []
                       }}
                       onReparse={handleReparse}
                     />
                   );
                 })()}
               </div>
             </motion.section>
           )}
         </AnimatePresence>
      </main>
       

      
  {/* Footer */}
  <Footer />   {/* ✅ render Footer here inside Home */}

</div>
  )
}

// ✅ Define Footer as a helper component (NOT export default)
function Footer() {
  const domain = typeof window !== "undefined" ? window.location.hostname : "";

  const isCom = domain.endsWith(".com");
  const isCa = domain.endsWith(".ca");
    const isLocal = domain === "localhost" || domain === "127.0.0.1";

  return (
    <motion.footer
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8, delay: 1 }}
      className="bg-card border-t border-border/50 relative z-10"
    >
      <div className="container-professional px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="space-y-4">
            <VTechLogo />
            <p className="text-muted-foreground max-w-sm leading-relaxed">
              {isCom &&
                "vTech is a Managed IT Services firm based in Washington DC, specializing in Cloud Computing and Managed Network Security solutions."}
              {isCa &&
                "vTech is a leading IT Services provider in Canada, offering Cloud Solutions and Cybersecurity services tailored for Canadian enterprises."}
              {!isCom && !isCa &&
                "vTech provides IT Services across multiple regions with expertise in Cloud and Security."}
            </p>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Contact Information</h3>
            <ul className="space-y-3 text-muted-foreground">
              {isCom && (
                <>
                  <li className="flex items-center space-x-3">
                    <MapPin className="h-5 w-5 text-primary flex-shrink-0" />
                    <span>1100 H St NW, STE 850, Washington, DC, 20005-5479</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <Phone className="h-5 w-5 text-primary" />
                    <span>+1 202-644-9774</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <Mail className="h-5 w-5 text-primary" />
                    <span>info@vtechsolution.com</span>
                  </li>
                </>
              )}

              {isCa && (
                <>
                   <li className="flex items-center space-x-3">
                    <MapPin className="h-5 w-5 text-primary flex-shrink-0" />
                    <span>302-1200 PemBina Highway, winnipeg,MB R3T 2A7 Canada</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <Phone className="h-5 w-5 text-primary" />
                    <span>431.801.8200</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <Mail className="h-5 w-5 text-primary" />
                    <span>info@vtechsolution.ca</span>
                  </li>
                </>
              )}
              {isLocal && (
                <>
                  <li className="flex items-center space-x-3">
                    <MapPin className="h-5 w-5 text-primary flex-shrink-0" />
                    <span>302-1200 PemBina Highway, winnipeg,MB R3T 2A7 Canada</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <Phone className="h-5 w-5 text-primary" />
                    <span>431.801.8200</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <Mail className="h-5 w-5 text-primary" />
                    <span>info@vtechsolution.ca</span>
                  </li>
                </>
              )}
            </ul>
          </div>
        </div>

        <div className="border-t border-border/50 mt-8 pt-6 text-center text-sm text-muted-foreground">
          © {new Date().getFullYear()} vTech Solution, Inc. All rights reserved.
        </div>
      </div>
    </motion.footer>
  );
}
